import { request } from './api';
import type { AxiosRequestConfig } from 'axios';

const LAB_PREFIX = '/lab';

// ---------- 通用请求封装 ----------

interface LabGetOptions {
    showError?: boolean;
}

const labGet = <T>(url: string, params?: Record<string, unknown>, options: LabGetOptions = {}): Promise<T> => {
    return request({ method: 'get', url: `${LAB_PREFIX}${url}`, params }, options.showError ?? true);
};

const labPost = <T>(url: string, data?: Record<string, unknown>, idempotencyKey?: string, params?: Record<string, unknown>): Promise<T> => {
    const config: AxiosRequestConfig = { method: 'post', url: `${LAB_PREFIX}${url}`, data };
    // 集体预约的取消等接口把 activity_id 放在 query 上，body 里带没用
    if (params) {
        config.params = params;
    }
    if (idempotencyKey) {
        config.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return request(config, true);
};

const labPut = <T>(url: string, data?: Record<string, unknown>): Promise<T> => {
    return request({ method: 'put', url: `${LAB_PREFIX}${url}`, data }, true);
};

// ============================
// C 端 - 我的券环境（V2: ticket-environments）
// ============================

export const getMyInstances = (params?: {
    activity_id?: string;
    status?: string;
    page?: number;
    page_size?: number;
}): Promise<LabPaginatedResponse<LabMyInstance>> => {
    return labGet('/me/instances', params as Record<string, unknown>);
};

export const updateTicketEnvironmentName = (
    ticketEnvId: string,
    customName: string
): Promise<LabApiResponse<{ ticket_env_id: string; environment_name: string; custom_name: string }>> => {
    return labPut(`/me/ticket-environments/${ticketEnvId}/name`, { custom_name: customName });
};

// ============================
// C 端 - 资源型券账户（V2: ticket-accounts/resource）
// ============================

/** 按活动分组返回；每个活动下的字段叫 `environments`，不是 accounts */
export const getMyTicketAccounts = (params?: { activity_id?: string }): Promise<LabApiResponse<{ activities: LabTicketAccountActivity[] }>> => {
    return labGet('/me/ticket-accounts', params as Record<string, unknown>);
};

// ============================
// C 端 - 可预约时段
// ============================

/**
 * `date_from` 是**北京日期**（后端库存按北京时间切片），
 * `timezone` 只影响每格 `display` 的渲染，不改变切片边界。
 * 天数会被后端按角色窗口截断：用户 7 天 / 活动组管理员 30 天 / 平台管理员 180 天。
 */
export const getReservationSlots = (params: {
    ticket_env_id: string;
    date_from: string;
    days?: number;
    timezone?: string;
}): Promise<LabApiResponse<LabReservationSlotsResponse>> => {
    return labGet('/reservation-slots', params as Record<string, unknown>);
};

// ============================
// C 端 - 我的预约
// ============================

export const getMyReservations = (params?: {
    activity_id?: string;
    ticket_env_id?: string;
    status?: string;
    start_from?: string;
    start_to?: string;
    page?: number;
    page_size?: number;
}): Promise<LabPaginatedResponse<LabReservationSummary>> => {
    return labGet('/me/reservations', params as Record<string, unknown>);
};

// ============================
// C 端 - 创建自主预约
// ============================

export const createReservation = (
    data: LabCreateReservationRequest,
    idempotencyKey: string
): Promise<LabApiResponse<LabCreateReservationResponse>> => {
    return labPost('/reservations', data as unknown as Record<string, unknown>, idempotencyKey);
};

// ============================
// C 端 - 取消自主预约
// ============================

export const cancelReservation = (
    reservationId: string,
    data: LabCancelReservationRequest,
    idempotencyKey: string
): Promise<LabApiResponse<LabCancelReservationResponse>> => {
    return labPost(`/reservations/${reservationId}/cancel`, data as unknown as Record<string, unknown>, idempotencyKey);
};

// ============================
// C 端 - 集体预约（活动组管理员）
// ============================

export const getMyCoordinatorActivities = (): Promise<LabApiResponse<{ items: LabCoordinatorActivity[] }>> => {
    return labGet('/me/coordinator-activities');
};

/** 活动集体券账户：activity_id 走 query，不是路径参数 */
export const getGroupTicketAccounts = (activityId: string, options?: LabGetOptions): Promise<LabApiResponse<{ items: LabGroupTicketAccount[] }>> => {
    return labGet('/group-ticket-accounts', { activity_id: activityId }, options);
};

export const getEligibleMemberEnvironments = (
    activityId: string,
    params?: { resource_spec_id?: number; lab_spec_id?: number; keyword?: string; page?: number; page_size?: number }
): Promise<LabPaginatedResponse<LabEligibleMemberEnv>> => {
    return labGet(`/activities/${activityId}/eligible-member-environments`, params as Record<string, unknown>);
};

/**
 * 集体预约时段网格：按已选成员的券环境汇总人数与库存需求。
 * 与 C 端个人时段一样，`date_from` 是北京日期，`timezone` 只管展示。
 */
export const getGroupReservationSlots = (params: {
    activity_id: string;
    ticket_env_ids: string[];
    date_from: string;
    days?: number;
    timezone?: string;
}): Promise<LabApiResponse<LabGroupSlotsResponse>> => {
    return labPost('/group-reservations/slots', params as unknown as Record<string, unknown>);
};

export const previewGroupReservation = (data: LabGroupReservationPreviewRequest): Promise<LabApiResponse<LabGroupReservationPreviewResponse>> => {
    return labPost('/group-reservations/preview', data as unknown as Record<string, unknown>);
};

export const createGroupReservation = (
    data: LabCreateGroupReservationRequest,
    idempotencyKey: string
): Promise<LabApiResponse<LabCreateGroupReservationResponse>> => {
    return labPost('/group-reservations', data as unknown as Record<string, unknown>, idempotencyKey);
};

/** 集体预约批次列表 */
export const getGroupReservationBatches = (params: {
    activity_id: string;
    status?: string;
    resource_spec_id?: number;
    start_from?: string;
    start_to?: string;
    page?: number;
    page_size?: number;
}): Promise<LabPaginatedResponse<LabGroupReservationRecord>> => {
    return labGet('/group-reservations', params as Record<string, unknown>);
};

/** 集体预约批次详情：activity_id 是必填 query */
export const getGroupReservationBatchDetail = (
    batchId: string,
    activityId: string
): Promise<LabApiResponse<{ items: LabReservationSummary[] } & Record<string, unknown>>> => {
    return labGet(`/group-reservations/${batchId}`, { activity_id: activityId });
};

/**
 * 局部取消集体预约：按**批次**取消其中若干条预约。
 * 路径上是 batch_id 不是 reservation_id，activity_id 必须放 query。
 */
export const cancelGroupReservation = (
    batchId: string,
    activityId: string,
    data: { reservation_ids: string[]; reason?: string },
    idempotencyKey: string
): Promise<LabApiResponse<LabCancelGroupReservationResponse>> => {
    return labPost(`/group-reservations/${batchId}/cancel`, data as unknown as Record<string, unknown>, idempotencyKey, { activity_id: activityId });
};

// ============================
// C 端 - 实例操作
// ============================

/** 预约窗口内手动开机 */
export const startInstance = (
    instanceId: string,
    data: LabShutdownInstanceRequest,
    idempotencyKey: string
): Promise<LabApiResponse<LabInstanceActionResponse>> => {
    return labPost(`/me/instances/${instanceId}/start`, data as unknown as Record<string, unknown>, idempotencyKey);
};

/** 当前业务使用期内手动关机 */
export const shutdownInstance = (
    instanceId: string,
    data: LabShutdownInstanceRequest,
    idempotencyKey: string
): Promise<LabApiResponse<LabInstanceActionResponse>> => {
    return labPost(`/me/instances/${instanceId}/shutdown`, data as unknown as Record<string, unknown>, idempotencyKey);
};

/** 当前业务使用期内重置实例（会丢失容器内数据，调用前必须二次确认） */
export const resetInstance = (
    instanceId: string,
    data: LabShutdownInstanceRequest,
    idempotencyKey: string
): Promise<LabApiResponse<LabInstanceActionResponse>> => {
    return labPost(`/me/instances/${instanceId}/reset`, data as unknown as Record<string, unknown>, idempotencyKey);
};

/** 失败实例重建；后端按预约型/直配型选择受约束的恢复实现 */
export const rebuildInstance = (
    instanceId: string,
    data: LabShutdownInstanceRequest,
    idempotencyKey: string
): Promise<LabApiResponse<LabInstanceActionResponse>> => {
    return labPost(`/me/instances/${instanceId}/rebuild`, data as unknown as Record<string, unknown>, idempotencyKey);
};

/** 更新直配实例名称；预约型仍使用 ticket-environment 名称接口 */
export const updateDirectInstanceName = (instanceId: string, customName: string): Promise<LabApiResponse<Record<string, unknown>>> => {
    return labPut(`/me/instances/${instanceId}/name`, { custom_name: customName });
};

// ============================
// C 端 - 任务查询
// ============================

export const getTaskStatus = (taskId: string): Promise<LabApiResponse<LabTaskRef>> => {
    return labGet(`/tasks/${taskId}`, undefined, { showError: false });
};

export async function getAllLabPages<T>(
    fetchPage: (page: number, pageSize: number) => Promise<LabPaginatedResponse<T>>,
    pageSize = 100
): Promise<T[]> {
    const first = await fetchPage(1, pageSize);
    const items = [...(first.data?.items || [])];
    const effectivePageSize = first.data?.page_size || pageSize;
    const totalPages = Math.ceil((first.data?.total || 0) / effectivePageSize);

    for (let page = 2; page <= totalPages; page++) {
        const response = await fetchPage(page, effectivePageSize);
        items.push(...(response.data?.items || []));
    }

    return items;
}
